import Company from "./company";
import JobPostService from "./job-post-service";
import Article from "./article";
import { DateTime } from "luxon";
import JobPostLink from "./job-post-link";

// Type for MDX-based job posts frontmatter (from JobPostMdxFrontmatter GraphQL type)
export interface JobPostMdxFrontmatter {
  id?: string;
  title?: string;
  type?: string;
  company: any; // CompanyYaml type
  locations?: any[];
  employmentTypes?: any[];
  categories?: any[];
  services?: any[];
  links?: any[];
  labels?: any[];
  references?: any[];
  relatedArticles?: any[];
  createdAt: string;
  updatedAt: string;
  closed?: boolean;
  isAutoGenerated?: boolean;
  minimumSalary?: number;
  maximumSalary?: number;
  tools?: string[];
  applicationUrl?: string;
  source?: string;
}

// Type for MDX-based job posts (from JobPostMdx GraphQL type)
export interface MdxJobPost {
  id: string;
  frontmatter: JobPostMdxFrontmatter;
  body?: string;
  excerpt?: string;
}

export default class JobPost {
  id: string;
  title: string;
  company: Company;
  categories: JobPostCategory[];
  services: JobPostService[];
  labels: JobPostLabel[];
  employmentTypes: JobPostEmploymentType[];
  locations: JobPostLocation[];
  minimumSalary: number | null;
  maximumSalary: number | null;
  tools: string[] | null;
  content: any | null;
  body: string | null;
  links: any;
  references: any[];
  relatedArticles: Article[];
  applicationUrl: string | null;
  closed: boolean;
  isAutoGenerated: boolean;
  source: string | null;
  updatedAt: DateTime;
  createdAt: DateTime;

  constructor(jobPost: MdxJobPost) {
    const jobPostData = jobPost.frontmatter;

    this.id = jobPostData.id!;
    this.title = jobPostData.title ?? "";
    this.company = new Company(jobPostData.company!);
    this.body = jobPost.body || null;
    this.categories = jobPostData.categories
      ? jobPostData.categories.map(
          (category: any) => new JobPostCategory(category!)
        )
      : [];
    this.locations = jobPostData.locations
      ? jobPostData.locations.map(
          (location: any) => new JobPostLocation(location!)
        )
      : [];
    this.employmentTypes = jobPostData.employmentTypes
      ? jobPostData.employmentTypes.map(
          (type: any) => new JobPostEmploymentType(type!)
        )
      : [];
    this.services = jobPostData.services
      ? jobPostData.services.map((service: any) => new JobPostService(service!))
      : [];
    this.labels = jobPostData.labels
      ? jobPostData.labels.map((label: any) => new JobPostLabel(label!))
      : [];
    this.minimumSalary = jobPostData.minimumSalary ?? null;
    this.maximumSalary = jobPostData.maximumSalary ?? null;
    this.tools = jobPostData.tools ?? null;
    this.applicationUrl = jobPostData.applicationUrl ?? null;
    this.content = jobPost.body ? jobPost.body : null;
    this.links =
      jobPostData.links?.map((link: any) => new JobPostLink(link!)) ?? [];
    this.references = jobPostData.references || [];
    this.relatedArticles = jobPostData.relatedArticles
      ? jobPostData.relatedArticles.map((article: any) => new Article(article!))
      : [];
    this.closed = jobPostData.closed || false;
    this.isAutoGenerated = jobPostData.isAutoGenerated || false;
    this.source = jobPostData.source || null;
    this.updatedAt = DateTime.fromISO(jobPostData.updatedAt!).setZone(
      "Asia/Tokyo"
    );
    this.createdAt = DateTime.fromISO(jobPostData.createdAt!).setZone(
      "Asia/Tokyo"
    );
  }
}

export class JobPostLabel {
  id: string;
  name: string;
  slug: string;

  constructor(label: { id: string; name: string; slug: string }) {
    this.id = label.id;
    this.name = label.name!;
    this.slug = label.slug!;
  }
}

export class JobPostCategory {
  id: string;
  name: string;
  slug: string;

  constructor(label: { id: string; name: string; slug: string }) {
    this.id = label.id;
    this.name = label.name!;
    this.slug = label.slug!;
  }
}

export class JobPostLocation {
  id: string;
  name: string;
  slug: string;

  constructor(label: { id: string; name: string; slug: string }) {
    this.id = label.id;
    this.name = label.name!;
    this.slug = label.slug!;
  }
}

export class JobPostEmploymentType {
  id: string;
  name: string;
  slug: string;

  constructor(label: { id: string; name: string; slug: string }) {
    this.id = label.id;
    this.name = label.name!;
    this.slug = label.slug!;
  }
}
